import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, Search } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  SectionTitle,
  Spinner,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { loadNaverMaps } from '@/lib/naver-map';
import type { ChildSummary } from '@/types';

interface Vehicle {
  id: string;
  name: string;
  plateNo: string;
  capacity: number;
}

interface RouteStop {
  id: string;
  name: string;
  order: number;
  lat: number | null;
  lng: number | null;
  arriveAt: string | null;
}

interface RouteView {
  id: string;
  name: string;
  vehicle: Vehicle | null;
  driver: { memberId: string; name: string } | null;
  stops: RouteStop[];
  children: { childId: string; name: string; stopId: string | null }[];
}

interface Member {
  id: string;
  role: string;
  user: { name: string };
}

/** 버스 관리 — 명세서 §5.20 (원장). 차량·노선·정거장·탑승 원아·기사 배정. */
export function BusManagePage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const vehicles = useQuery({
    queryKey: ['bus', 'vehicles'],
    queryFn: () => apiData<Vehicle[]>('/bus/vehicles'),
  });
  const routes = useQuery({
    queryKey: ['bus', 'routes'],
    queryFn: () => apiData<RouteView[]>('/bus/routes'),
  });
  const members = useQuery({
    queryKey: ['facility', 'members'],
    queryFn: () => apiData<Member[]>('/facilities/current/members'),
  });
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['bus'] });
  const onError = (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.');

  // 차량 등록
  const [vehicleName, setVehicleName] = useState('');
  const [plateNo, setPlateNo] = useState('');
  const addVehicle = useMutation({
    mutationFn: () =>
      apiData('/bus/vehicles', { method: 'POST', body: { name: vehicleName, plateNo } }),
    onSuccess: () => {
      setVehicleName('');
      setPlateNo('');
      invalidate();
    },
    onError,
  });

  // 노선 등록
  const [routeName, setRouteName] = useState('');
  const addRoute = useMutation({
    mutationFn: () => apiData('/bus/routes', { method: 'POST', body: { name: routeName } }),
    onSuccess: () => {
      setRouteName('');
      invalidate();
    },
    onError,
  });

  if (vehicles.isPending || routes.isPending) return <Spinner />;
  if (vehicles.isError || routes.isError) {
    return (
      <Screen withNav={false}>
        <AppBar title="버스 관리" back />
        <ErrorNote
          message="버스 정보를 불러오지 못했어요."
          onRetry={() => {
            void vehicles.refetch();
            void routes.refetch();
          }}
        />
      </Screen>
    );
  }

  // 원장이 직접 모는 원도 많다 — 기사·교사·원장 모두 운행 담당이 될 수 있다
  const drivers = (members.data ?? []).filter(
    (m) => m.role === 'DRIVER' || m.role === 'TEACHER' || m.role === 'DIRECTOR',
  );

  return (
    <Screen withNav={false}>
      <AppBar title="버스 관리" back />

      <div className="flex flex-col gap-4 p-4">
        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <section>
          <SectionTitle>차량</SectionTitle>
          <Card className="flex flex-col gap-2">
            {(vehicles.data ?? []).map((vehicle) => (
              <p key={vehicle.id} className="text-base">
                {vehicle.name} <span className="text-ink-sub">· {vehicle.plateNo}</span>
              </p>
            ))}
            <div className="flex gap-2">
              <Input
                placeholder="차량 이름 (예: 1호차)"
                value={vehicleName}
                onChange={(e) => setVehicleName(e.target.value)}
              />
              <Input
                placeholder="번호판"
                className="w-36"
                value={plateNo}
                onChange={(e) => setPlateNo(e.target.value)}
              />
              <Button
                icon={<Plus size={16} />}
                disabled={!vehicleName.trim() || !plateNo.trim()}
                loading={addVehicle.isPending}
                onClick={() => addVehicle.mutate()}
              >
                추가
              </Button>
            </div>
          </Card>
        </section>

        <section>
          <SectionTitle>노선</SectionTitle>
          <div className="mb-2 flex gap-2">
            <Input
              placeholder="노선 이름 (예: A코스)"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
            />
            <Button
              icon={<Plus size={16} />}
              disabled={!routeName.trim()}
              loading={addRoute.isPending}
              onClick={() => addRoute.mutate()}
            >
              추가
            </Button>
          </div>

          {(routes.data ?? []).length === 0 && (
            <EmptyState title="노선이 없어요" body="노선을 만들고 기사님·정거장·원아를 배정해 보세요." />
          )}

          {(routes.data ?? []).map((route) => (
            <RouteCard
              key={route.id}
              route={route}
              vehicles={vehicles.data ?? []}
              drivers={drivers}
              childrenList={children.data ?? []}
              onError={onError}
              onSaved={invalidate}
            />
          ))}
        </section>
      </div>
    </Screen>
  );
}

function RouteCard({
  route,
  vehicles,
  drivers,
  childrenList,
  onError,
  onSaved,
}: {
  route: RouteView;
  vehicles: Vehicle[];
  drivers: Member[];
  childrenList: ChildSummary[];
  onError: (caught: unknown) => void;
  onSaved: () => void;
}) {
  const [stopsText, setStopsText] = useState(route.stops.map((s) => s.name).join(', '));
  const [selected, setSelected] = useState<string[]>(route.children.map((c) => c.childId));
  // 정거장별 "위도, 경도" 초안 — 실시간 위치 지도·도착 임박 판정에 쓴다 (§5.20 P6)
  const [coordDrafts, setCoordDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      route.stops.map((s) => [s.id, s.lat != null && s.lng != null ? `${s.lat}, ${s.lng}` : '']),
    ),
  );

  const update = useMutation({
    mutationFn: (body: { vehicleId?: string; driverMemberId?: string }) =>
      apiData(`/bus/routes/${route.id}`, { method: 'PATCH', body: { name: route.name, ...body } }),
    onSuccess: onSaved,
    onError,
  });

  const saveStops = useMutation({
    mutationFn: () =>
      apiData(`/bus/routes/${route.id}/stops`, {
        method: 'PUT',
        body: {
          stops: stopsText
            .split(',')
            .map((name) => name.trim())
            .filter(Boolean)
            .map((name) => {
              // 이름이 같은 기존 정거장의 좌표·도착 시각은 유지한다
              const existing = route.stops.find((s) => s.name === name);
              return {
                name,
                lat: existing?.lat ?? undefined,
                lng: existing?.lng ?? undefined,
                arriveAt: existing?.arriveAt ?? undefined,
              };
            }),
        },
      }),
    onSuccess: onSaved,
    onError,
  });

  const saveCoords = useMutation({
    mutationFn: () =>
      apiData(`/bus/routes/${route.id}/stops`, {
        method: 'PUT',
        body: {
          stops: route.stops.map((stop) => {
            const parsed = parseLatLng(coordDrafts[stop.id] ?? '');
            return {
              name: stop.name,
              arriveAt: stop.arriveAt ?? undefined,
              lat: parsed?.lat,
              lng: parsed?.lng,
            };
          }),
        },
      }),
    onSuccess: onSaved,
    onError,
  });

  const saveChildren = useMutation({
    mutationFn: () =>
      apiData(`/bus/routes/${route.id}/children`, {
        method: 'PUT',
        body: { children: selected.map((childId) => ({ childId })) },
      }),
    onSuccess: onSaved,
    onError,
  });

  return (
    <Card className="mb-3 flex flex-col gap-3">
      <p className="font-semibold">{route.name}</p>

      <FieldGroup label="차량">
        <div className="flex flex-wrap gap-1.5">
          {vehicles.map((vehicle) => (
            <Chip
              key={vehicle.id}
              tone={route.vehicle?.id === vehicle.id ? 'primary' : 'neutral'}
              selected={route.vehicle?.id === vehicle.id}
              onClick={() => update.mutate({ vehicleId: vehicle.id })}
            >
              {vehicle.name}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="운행 담당 (기사·교사·원장)">
        <div className="flex flex-wrap gap-1.5">
          {drivers.map((member) => (
            <Chip
              key={member.id}
              tone={route.driver?.memberId === member.id ? 'primary' : 'neutral'}
              selected={route.driver?.memberId === member.id}
              onClick={() => update.mutate({ driverMemberId: member.id })}
            >
              {member.user.name}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <Field label="정거장 (쉼표로 순서대로)">
        <div className="flex gap-2">
          <Input value={stopsText} onChange={(e) => setStopsText(e.target.value)} placeholder="예: 행복아파트, 시장 앞, 원" />
          <Button size="sm" loading={saveStops.isPending} onClick={() => saveStops.mutate()}>
            저장
          </Button>
        </div>
      </Field>

      {/* 좌표가 있어야 실시간 지도·도착 임박 알림이 동작한다 (§5.20 P6) */}
      {route.stops.length > 0 && (
        <FieldGroup label="정거장 위치 (주소로 찾고, 지도에서 핀을 맞춰 보정)">
          <div className="flex flex-col gap-1.5">
            {route.stops.map((stop) => (
              <StopCoordRow
                key={stop.id}
                stop={stop}
                value={coordDrafts[stop.id] ?? ''}
                onChange={(value) =>
                  setCoordDrafts((prev) => ({ ...prev, [stop.id]: value }))
                }
              />
            ))}
          </div>
          <Button
            size="sm"
            className="mt-2"
            loading={saveCoords.isPending}
            onClick={() => saveCoords.mutate()}
          >
            위치 저장
          </Button>
        </FieldGroup>
      )}

      <FieldGroup label={`탑승 원아 (${selected.length}명)`}>
        <div className="flex flex-wrap gap-1.5">
          {childrenList.map((child) => {
            const on = selected.includes(child.id);
            return (
              <Chip
                key={child.id}
                tone={on ? 'primary' : 'neutral'}
                selected={on}
                onClick={() =>
                  setSelected((prev) =>
                    on ? prev.filter((id) => id !== child.id) : [...prev, child.id],
                  )
                }
              >
                {child.name}
              </Chip>
            );
          })}
        </div>
        <Button
          size="sm"
          className="mt-2"
          loading={saveChildren.isPending}
          onClick={() => saveChildren.mutate()}
        >
          탑승 원아 저장
        </Button>
      </FieldGroup>
    </Card>
  );
}

/** "위도, 경도" 입력 파싱 — 형식이 어긋나면 좌표 없음으로 저장한다 */
function parseLatLng(text: string): { lat: number; lng: number } | null {
  const parts = text.split(',').map((part) => Number(part.trim()));
  if (parts.length !== 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const [lat, lng] = parts;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/**
 * 정거장 위치 한 칸 — 원장 실사용 흐름(§5.20):
 *   ① 주소 검색(서버 지오코딩)으로 대략의 위치를 잡고
 *   ② 지도를 움직여 중앙 핀에 정확한 승하차 지점을 맞춰 보정한다.
 * 좌표 숫자는 화면에 참고로만 보여 준다 — 직접 입력은 지도 키가 없을 때만 연다.
 */
function StopCoordRow({
  stop,
  value,
  onChange,
}: {
  stop: RouteStop;
  value: string;
  onChange: (value: string) => void;
}) {
  const [addressOpen, setAddressOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<
    { roadAddress: string | null; jibunAddress: string | null; lat: number; lng: number }[] | null
  >(null);
  const [message, setMessage] = useState<string | null>(null);

  const coords = parseLatLng(value);

  const search = useMutation({
    mutationFn: (q: string) =>
      apiData<{ roadAddress: string | null; jibunAddress: string | null; lat: number; lng: number }[]>(
        `/bus/geocode?query=${encodeURIComponent(q)}`,
      ),
    onSuccess: (rows) => {
      setResults(rows);
      setMessage(rows.length === 0 ? '검색 결과가 없어요. 도로명 주소로 입력해 보세요.' : null);
    },
    onError: (caught) =>
      setMessage(caught instanceof ApiError ? caught.message : '주소 검색에 실패했어요.'),
  });

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-surface p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-semibold">{stop.name}</p>
        <span className="shrink-0 text-xs text-ink-sub tnum">
          {coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : '위치 미지정'}
        </span>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          icon={<Search size={14} />}
          onClick={() => {
            setAddressOpen((prev) => !prev);
            setMapOpen(false);
          }}
        >
          주소 검색
        </Button>
        <Button
          size="sm"
          variant="outline"
          icon={<MapPin size={14} />}
          onClick={() => {
            setMapOpen((prev) => !prev);
            setAddressOpen(false);
          }}
        >
          지도에서 보정
        </Button>
      </div>

      {addressOpen && (
        <div className="flex flex-col gap-1.5 rounded-sm bg-bg p-2">
          <div className="flex gap-2">
            <Input
              placeholder="예: 대구 중구 새싹로 1"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Button
              size="sm"
              loading={search.isPending}
              disabled={query.trim().length < 2}
              onClick={() => search.mutate(query)}
            >
              검색
            </Button>
          </div>
          {message && <p className="text-sm text-ink-sub">{message}</p>}
          {(results ?? []).map((result, index) => (
            <button
              key={index}
              type="button"
              className="tap rounded-sm bg-surface px-3 py-2 text-left text-sm shadow-card"
              onClick={() => {
                onChange(`${result.lat}, ${result.lng}`);
                setAddressOpen(false);
                setResults(null);
                // 주소는 대략의 위치다 — 곧바로 지도를 열어 핀을 맞추게 한다.
                setMapOpen(true);
              }}
            >
              {result.roadAddress ?? result.jibunAddress}
            </button>
          ))}
        </div>
      )}

      {mapOpen && (
        <MapPicker
          initial={coords}
          onPick={(point) => {
            onChange(`${point.lat}, ${point.lng}`);
            setMapOpen(false);
          }}
          onClose={() => setMapOpen(false)}
          fallback={
            <div className="flex flex-col gap-1.5 rounded-sm bg-bg p-2">
              <p className="text-sm text-ink-sub">
                지도 키가 설정되지 않아 숫자로 입력해요. (위도, 경도)
              </p>
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="35.8714, 128.6014"
              />
            </div>
          }
        />
      )}
    </div>
  );
}

/**
 * 지도 위치 보정 (§5.20) — 지도를 움직여 중앙의 고정 핀에 지점을 맞추는 방식.
 * 터치 화면에서 마커 드래그보다 정확하고 쉬워서 이 패턴을 쓴다.
 * 네이버 지도 키가 없으면 fallback(숫자 입력)을 보여 준다.
 */
function MapPicker({
  initial,
  onPick,
  onClose,
  fallback,
}: {
  initial: { lat: number; lng: number } | null;
  onPick: (point: { lat: number; lng: number }) => void;
  onClose: () => void;
  fallback: React.ReactNode;
}) {
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ map: { getCenter: () => { lat: () => number; lng: () => number } } } | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');

  useEffect(() => {
    let cancelled = false;
    void loadNaverMaps().then((naver) => {
      if (cancelled) return;
      if (!naver?.maps || !mapEl.current) {
        setState('unavailable');
        return;
      }
      // 위치가 없으면 남한 전체가 보이는 축척에서 시작해 확대해 들어간다.
      const map = new naver.maps.Map(mapEl.current, {
        center: new naver.maps.LatLng(initial?.lat ?? 36.4, initial?.lng ?? 127.8),
        zoom: initial ? 17 : 7,
        mapDataControl: false,
        scaleControl: false,
      });
      mapRef.current = { map };
      setState('ready');
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'unavailable') return <>{fallback}</>;

  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-64 overflow-hidden rounded-md border border-line-strong">
        <div ref={mapEl} className="h-full w-full" />
        {/* 중앙 고정 핀 — 지도를 움직여 핀 끝에 승하차 지점을 맞춘다 */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full drop-shadow">
          <MapPin size={34} className="fill-secondary text-primary-strong" strokeWidth={2.2} />
        </div>
        {state === 'loading' && (
          <div className="absolute inset-0 grid place-items-center bg-bg/70">
            <Spinner label="지도를 여는 중이에요" />
          </div>
        )}
      </div>
      <p className="text-xs text-ink-sub">지도를 움직여 핀 끝을 승하차 지점에 맞춘 뒤 지정해 주세요.</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" block onClick={onClose}>
          취소
        </Button>
        <Button
          size="sm"
          block
          disabled={state !== 'ready'}
          onClick={() => {
            const center = mapRef.current?.map.getCenter();
            if (center) onPick({ lat: center.lat(), lng: center.lng() });
          }}
        >
          이 위치로 지정
        </Button>
      </div>
    </div>
  );
}
